import dayjs from 'dayjs';
import _uniqBy from 'lodash/uniqBy';
import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import { useQuery, useSelector } from 'hooks';
import { DATE_FORMAT } from 'consts';
import { routes } from 'configs';
import { TrustDiscretionary } from 'types';
import { getQueryKey } from 'utils';

import { ExtendedTable, NavLink, StatusTag } from 'components';

interface TrustDiscretionaryListItem extends TrustDiscretionary {
  trusteeType: string;
  status: string;
}

const useStyles = createUseStyles({
  row: {
    cursor: 'pointer',
  },
});

const DiscretionaryTrustList: FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const cognitoID = useSelector(
    (state) => state.auth.client?.cognitoID as string,
  );

  const queryKey = getQueryKey('discretionaryTrustList', cognitoID);

  const { data = [], isLoading } = useQuery<TrustDiscretionaryListItem[]>({
    apiName: 'client',
    queryKey,
    path: `/${cognitoID}/discretionary-trust`,
  });

  return (
    <ExtendedTable<TrustDiscretionaryListItem>
      loading={isLoading}
      dataSource={data}
      onRow={(record) => ({
        className: classes.row,
        onClick: () => navigate(routes.discretionaryTrust.details(record.id)),
      })}
      columns={[
        {
          dataIndex: 'trustName',
          title: 'common.trustName',
          render: ({ value, record }) => (
            <NavLink to={routes.discretionaryTrust.details(record.id)}>
              {value}
            </NavLink>
          ),
        },
        {
          title: 'common.status',
          dataIndex: 'status',
          filters: _uniqBy(data, 'status').map((item) => ({
            value: item.statusID,
            text: item.status,
          })),
          onFilter: (value, record) =>
            value.toString() === record.statusID.toString(),
          render: ({ record: { statusID, status } }) => (
            <StatusTag id={statusID} name={status} />
          ),
        },
        {
          title: 'discretionaryTrust.deedDate',
          dataIndex: 'deedDate',
          render: ({ value }) => dayjs(value).format(DATE_FORMAT),
        },
      ]}
    />
  );
};

export default DiscretionaryTrustList;
